import React, { useEffect, useRef, useState } from 'react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  UserCircleIcon,
  KeyIcon,
  PaperAirplaneIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import type { PlatformTenant } from './TenantAccordionItem';

type TenantActionMenuProps = {
  tenant: PlatformTenant;
  onEdit: () => void;
  onToggleStatus: () => void;
  onImpersonate: () => void;
  onResetPassword: () => void;
  onResendCredentials: () => void;
  onCreateAdmin: () => void;
  onViewIncidents: () => void;
  onViewAudit: () => void;
};

type MenuAction = {
  id: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  danger?: boolean;
  onClick: () => void;
};

export const TenantActionMenu: React.FC<TenantActionMenuProps> = ({
  tenant,
  onEdit,
  onToggleStatus,
  onImpersonate,
  onResetPassword,
  onResendCredentials,
  onCreateAdmin,
  onViewIncidents,
  onViewAudit
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocumentClick);
    return () => {
      document.removeEventListener('click', onDocumentClick);
    };
  }, []);

  const actions: MenuAction[] = [
    { id: 'edit', label: 'Editar funeraria', icon: PencilSquareIcon, onClick: onEdit },
    {
      id: 'suspend',
      label: tenant.isActive ? 'Suspender funeraria' : 'Reactivar funeraria',
      icon: tenant.isActive ? PauseCircleIcon : PlayCircleIcon,
      danger: tenant.isActive,
      onClick: onToggleStatus
    },
    { id: 'impersonate', label: 'Entrar como soporte', icon: UserCircleIcon, onClick: onImpersonate },
    { id: 'reset-admin-password', label: 'Resetear clave admin', icon: KeyIcon, onClick: onResetPassword },
    {
      id: 'resend-credentials',
      label: 'Reenviar credenciales',
      icon: PaperAirplaneIcon,
      onClick: onResendCredentials
    },
    {
      id: 'create-admin',
      label: 'Crear usuario administrador',
      icon: UserPlusIcon,
      onClick: onCreateAdmin
    },
    { id: 'incidents', label: 'Ver incidencias', icon: ExclamationTriangleIcon, onClick: onViewIncidents },
    { id: 'audit', label: 'Ver auditoría', icon: DocumentTextIcon, onClick: onViewAudit }
  ];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
        title="Más acciones"
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-20 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl shadow-slate-900/15">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                action.onClick();
              }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition ${
                action.danger
                  ? 'text-rose-700 hover:bg-rose-50'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <action.icon className="h-4 w-4 shrink-0" aria-hidden />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
